import React, { FunctionComponent, RefObject } from 'react';
import styled from 'styled-components';
import closeImg from '../../../../assets/icons/close.svg';
import leftArrow from '../../../../assets/icons/mobile-left-arrow.svg';
import { MenuItem, sendClickOnMenuEvent } from '../MenuUtils';
import { SortType } from '../MenuUtils';
import loadable from '@loadable/component';
import MenuMobileFirstLevel from './MenuMobileFirstLevel';
import { InstaStories } from '../../../common/components/loaders/MenuLoader';
import { useTranslation } from 'react-i18next';

export interface MobileContentProps {
  show: boolean;
  rootMenuItems: MenuItem[];
  closeMenu: () => void;
  menuSelectedPath: MenuItem[];
  setMenuSelectedPath: (menuItems: MenuItem[]) => void;
  sorting: SortType;
  setSorting: (sortType: SortType) => void;
  thematicsFilter: string;
  setThematicsFilter: (filter: string) => void;
  isMinimalistHeader: boolean;
}
const MenuCategoryContent = loadable(() => import('../components/MenuCategoryContent'), { fallback: <InstaStories /> });
const MenuMobileSecondLevel = loadable(() => import('./MenuMobileSecondLevel'), { fallback: <InstaStories /> });

const MenuMobileContent: FunctionComponent<MobileContentProps> = ({
  show,
  rootMenuItems,
  menuSelectedPath,
  setMenuSelectedPath,
  closeMenu,
  sorting,
  setSorting,
  setThematicsFilter,
  thematicsFilter,
  isMinimalistHeader
}) => {
  const { t } = useTranslation('sharedComponents');
  const currentLevel = menuSelectedPath.length;
  const componentRef: RefObject<HTMLDivElement> = React.createRef();

  if (show) {
    MenuCategoryContent.preload();
    MenuMobileSecondLevel.preload();
  }
  const returnLevelUp = () => {
    setMenuSelectedPath(menuSelectedPath.slice(0, -1));
  };

  const onPanelClick = (index: number, label: string) => {
    sendClickOnMenuEvent({ index, label });
  };

  return (
    <>
      <StyledFullscreen show={show}>
        <Header>
          <style>
            {`
         div[class^="RefreshBanner"]{
          display:none;
         }
        `}
          </style>
          {currentLevel > 0 && (
            <StyledBackButton onClick={returnLevelUp}>
              <img src={leftArrow} alt="back" />
              {t('shared.menu.back')}
            </StyledBackButton>
          )}

          <StyledCloseButton onClick={closeMenu}>
            <img src={closeImg} alt="close" />
          </StyledCloseButton>
        </Header>
        <Container ref={componentRef}>
          <StyledAnimatedPanel className={currentLevel == 0 ? 'visible' : ''}>
            {currentLevel == 0 && (
              <MenuMobileFirstLevel
                rootMenuItems={rootMenuItems}
                setMenuSelectedPath={setMenuSelectedPath}
                closeMenu={closeMenu}
                isMinimalistHeader={isMinimalistHeader}
              />
            )}
          </StyledAnimatedPanel>

          <StyledAnimatedPanel className={currentLevel == 1 ? 'visible' : ''}>
            {currentLevel == 1 && (
              <MenuMobileSecondLevel
                item={menuSelectedPath[menuSelectedPath.length - 1]}
                setMenuSelectedPath={setMenuSelectedPath}
                menuSelectedPath={menuSelectedPath}
                sorting={sorting}
                setSorting={setSorting}
                thematicsFilter={thematicsFilter}
                setThematicsFilter={setThematicsFilter}
                parentRef={componentRef}
                closeMenu={closeMenu}
                onClick={label => onPanelClick(currentLevel, label)}
              />
            )}
          </StyledAnimatedPanel>

          <StyledAnimatedPanel className={currentLevel >= 2 ? 'visible' : ''}>
            {currentLevel >= 2 && (
              <MenuCategoryContent
                item={menuSelectedPath[menuSelectedPath.length - 1]}
                menuSelectedPath={menuSelectedPath}
                currentMenuPath={menuSelectedPath}
                setMenuSelectedPath={setMenuSelectedPath}
                hideImage={true}
                closeMenu={closeMenu}
                mobile={{
                  onMobileClick: (label: string) => {
                    onPanelClick(currentLevel, label);
                  }
                }}
                onClick={() => {}}
              />
            )}
          </StyledAnimatedPanel>
        </Container>
      </StyledFullscreen>
      {show && <StyledOverlay />}
    </>
  );
};

const StyledFullscreen = styled.div<{ show: boolean }>`
  position: fixed;

  height: 100%;
  top: 0;

  bottom: 0;
  background: #fff;
  z-index: 10000;
  transition: all 0.5s;

  @media (max-width: 1168px) {
    width: 50%;
    left: ${props => (props.show ? '0%' : '-100%')};
  }
  @media (max-width: 568px) {
    width: 90%;
    left: ${props => (props.show ? '0%' : '-100%')};
  }
`;

const StyledAnimatedPanel = styled.div`
  position: relative;
  left: -100%;

  transition: all 0.4s;
  &.visible {
    left: 0px;
  }
`;

export const StyledOverlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(50, 50, 50, 0.6);
  z-index: 9999;
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: fixed;
  height: 50px;
  position: relative;
  border-bottom: 1px solid #ebebeb;

  .button-container {
    width: 70px;
    display: flex;

    a {
      display: flex;
      align-items: center;
    }
  }

  .header-logo {
    flex: 1;
  }

  img {
    height: 12px;
  }
`;

const StyledBackButton = styled.div`
  font-size: 14px;
  position: absolute;
  top: 0px;
  left: 0px;
  padding: 16px;
  cursor: pointer;
  img {
    vertical-align: text-top;
    margin-right: 8px;
    height: 20px;
    width: 20px;
  }
`;

const StyledCloseButton = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 16px;
  img {
    height: 12px;
    width: 12px;
  }
`;

const Container = styled.div`
  margin-top: 0px;
  display: flex;
  flex-direction: column;
  height: calc(100% - 60px);
  padding: 16px;
`;

export default MenuMobileContent;
